import React, { useEffect, useMemo, useState } from 'react';
import styles from './UpdatesRequired.module.scss';
import hideLoader from '@repo/utils/helpers/hide-loader';
import { DesktopUpdateStatus } from '@repo/utils/types';
import Button from '@components/Button';
import { Logo } from '@repo/icons';

export default function UpdateRequired() {
  const [updateStatus, setUpdateStatus] = useState<DesktopUpdateStatus | null>(null);
  // TODO Windows temporarily cannot perform automatic updates
  const canRequestUpdate =
    !!window.desktopIntegrations?.requestUpdate &&
    window.desktopIntegrations?.appInfo.os !== 'Windows';

  const updateText = useMemo(() => {
    if (!updateStatus) {
      return null;
    } else if (updateStatus === DesktopUpdateStatus.ready) {
      return 'Your update is ready. Clay will now restart to complete the installation.';
    } else if (updateStatus === DesktopUpdateStatus.downloading) {
      return 'We’re downloading the latest version of Clay.';
    } else if (updateStatus === DesktopUpdateStatus.unavailable) {
      return 'No update available.';
    } else if (updateStatus === DesktopUpdateStatus.checking) {
      return 'Checking for updates.';
    }
    return updateStatus;
  }, [updateStatus]);

  useEffect(() => {
    hideLoader();

    let unsub;
    // note: we'll always be electron... doing this for local dev
    if (window.app.isElectron && window.desktopIntegrations?.onUpdateEvent) {
      unsub = window.desktopIntegrations.onUpdateEvent((status) => {
        setUpdateStatus(status);
      });
    }

    return () => unsub?.();
  }, []);

  function onUpdateClick() {
    if (window.desktopIntegrations?.requestUpdate) {
      window.desktopIntegrations.requestUpdate();
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.logoContainer}>
        <div className={styles.gradient}>
          <svg width="178" height="178" viewBox="0 0 178 178" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter
                id="blurFilter"
                x="-3"
                y="-4"
                width="185"
                height="188"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                <feGaussianBlur stdDeviation="16.3636" />
              </filter>
              <linearGradient
                id="lg1"
                x1="30"
                y1="29"
                x2="153"
                y2="149"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FFA456" />
                <stop offset="1" stopColor="#FFA456" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="lg2"
                x1="150"
                y1="29"
                x2="89"
                y2="89"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#00B1EE" />
                <stop offset="1" stopColor="#00B1EE" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="lg3"
                x1="30"
                y1="151"
                x2="91"
                y2="91"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#EE0000" />
                <stop offset="1" stopColor="#EE00E1" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="lg4"
                x1="150"
                y1="151"
                x2="89"
                y2="91"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#8700FF" />
                <stop offset="1" stopColor="#EE00E1" stopOpacity="0" />
              </linearGradient>
            </defs>
            <g filter="url(#blurFilter)">
              <circle cx="89" cy="89" r="60" fill="url(#lg1)" />
              <circle cx="89" cy="89" r="60" fill="url(#lg2)" />
              <circle cx="89" cy="89" r="60" fill="url(#lg3)" />
              <circle cx="89" cy="89" r="60" fill="url(#lg4)" />
            </g>
          </svg>
        </div>
        <div className={styles.logo}>
          <Logo />
        </div>
      </div>

      <div className={styles.messageWrap}>
        <div className={styles.userMessage}>
          <h4>Upgrade to the latest version of Clay</h4>
          <p>We’ve released a brand new update.</p>
          {!canRequestUpdate && (
            <p>
              Download the new version{' '}
              <a href="https://clay.earth/start" target="_blank">
                here
              </a>
              .
            </p>
          )}
        </div>
        {updateStatus ? (
          <div>{updateText}</div>
        ) : (
          canRequestUpdate && (
            <div>
              <Button className={styles.button} onClick={onUpdateClick}>
                Update
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
}
